/**
 * Typed client for /inference-admin/* — backs the Inference Admin screen.
 *
 * Companion to the inference customer console at console.nurev.io: same
 * backend, different lens. The customer-facing console shows usage to
 * a single buyer; this admin surface shows it across all buyers, plus
 * the ops actions (create customer, mint key, revoke key, monitor jobs)
 * that customer-self-service intentionally doesn't expose.
 *
 * Money is stored as **microcents** (1/10,000,000 of a USD cent) on
 * the server so we never lose precision through accumulated rounding.
 * Display layer converts back to dollars.
 */
import { client, unwrap } from './client';

export type InferenceJobType = 'embeddings' | 'chat';
export type InferenceJobStatus = 'completed' | 'failed' | 'pending';
export type CustomerStatus = 'active' | 'trial' | 'suspended' | 'churned';

export interface InferenceUsageTotals {
  totalRequests: number;
  totalTokens: number;
  totalCost: number;   // microcents — paid to providers
  totalRevenue: number; // microcents — billed to customers
}

export interface InferenceProviderRow {
  _id: string;
  requests: number;
  tokens: number;
}

export interface InferenceModelRow {
  _id: string;
  tokens: number;
}

export interface InferenceUsageResponse {
  totals: InferenceUsageTotals;
  byProvider: InferenceProviderRow[];
  byModel: InferenceModelRow[];
}

export interface InferenceCustomer {
  _id: string;
  name: string;
  email: string;
  companyName?: string;
  status: CustomerStatus;
  billingPlan?: string;
  rateLimitRpm?: number;
  rateLimitTpm?: number;
  monthlySpend?: number; // cents
  testAccount?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InferenceApiKey {
  _id: string;
  customerId: string;
  name?: string;
  keyPrefix: string;
  status: 'active' | 'revoked';
  lastUsedAt?: string;
  createdAt: string;
}

export interface CustomerUsage30d {
  totalRequests: number;
  totalTokens: number;
  totalCostMicrocents: number;
  totalPriceMicrocents: number;
}

export interface InferenceCustomerListResponse {
  docs: InferenceCustomer[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface InferenceCustomerDetailResponse {
  customer: InferenceCustomer;
  apiKeys: InferenceApiKey[];
  usage30d: CustomerUsage30d;
}

/**
 * The create-customer endpoint returns the customer + a `apiKey` that's
 * shown exactly once. Persist behavior is the same as console.nurev.io's
 * key creation flow — display, then forget.
 */
export interface CreateInferenceCustomerResponse {
  customer: InferenceCustomer;
  apiKey: string;
}

export interface CreateInferenceApiKeyResponse {
  apiKey: string;
  keyId: string;
  keyPrefix: string;
}

export interface InferenceJob {
  _id: string;
  customerId?: string;
  type: InferenceJobType;
  model: string;
  provider?: string;
  status: InferenceJobStatus;
  totalTokens?: number;
  latencyMs?: number;
  priceMicrocents?: number;
  costMicrocents?: number;
  testTraffic?: boolean;
  createdAt: string;
}

export interface InferenceJobListResponse {
  docs: InferenceJob[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

// ─── Fetchers + mutations ─────────────────────────────────────────────────

export async function fetchInferenceUsage(
  days = 7
): Promise<InferenceUsageResponse> {
  return unwrap<InferenceUsageResponse>(client.get('/inference-admin/usage', {
    params: { days }
  }));
}

export async function fetchInferenceCustomers(params: {
  page?: number;
  limit?: number;
}): Promise<InferenceCustomerListResponse> {
  return unwrap<InferenceCustomerListResponse>(client.get(
    '/inference-admin/customers',
    { params }
  ));
}

export async function fetchInferenceCustomer(
  id: string
): Promise<InferenceCustomerDetailResponse> {
  return unwrap<InferenceCustomerDetailResponse>(client.get(
    `/inference-admin/customers/${id}`
  ));
}

export interface CreateCustomerInput {
  name: string;
  email: string;
  companyName?: string;
}

export async function createInferenceCustomer(
  input: CreateCustomerInput
): Promise<CreateInferenceCustomerResponse> {
  return unwrap<CreateInferenceCustomerResponse>(client.post(
    '/inference-admin/customers',
    input
  ));
}

export async function createInferenceApiKey(input: {
  customerId: string;
  name?: string;
}): Promise<CreateInferenceApiKeyResponse> {
  const { customerId, ...body } = input;
  return unwrap<CreateInferenceApiKeyResponse>(client.post(
    `/inference-admin/customers/${customerId}/keys`,
    body
  ));
}

export async function revokeInferenceApiKey(input: {
  customerId: string;
  keyId: string;
}): Promise<{ success: boolean }> {
  return unwrap<{ success: boolean }>(client.delete(
    `/inference-admin/customers/${input.customerId}/keys/${input.keyId}`
  ));
}

export async function fetchInferenceJobs(params: {
  page?: number;
  limit?: number;
}): Promise<InferenceJobListResponse> {
  return unwrap<InferenceJobListResponse>(client.get(
    '/inference-admin/jobs',
    { params }
  ));
}
